import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { DOTS_DEFAULTS, REELS_DEFAULTS } from "@/lib/sectionVariants";
import BannerDots from "@/components/site/BannerDots";

const selCls = "px-2 py-1.5 rounded-lg border border-border bg-background outline-none focus:border-primary text-sm w-full";

const Row = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground">
    {label}
    {children}
  </label>
);

const Toggle = ({ label, checked, onChange, testId }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2">
    <span className="text-xs font-semibold text-slate-700">{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

const Select = ({ value, onChange, options, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={selCls} data-testid={testId}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export const ReelsSettings = ({ section, onChange }) => {
  const c = { ...REELS_DEFAULTS, ...section };
  const set = (k) => (v) => onChange({ [k]: v });
  return (
    <div className="mt-3 pl-9 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reels-settings">
      <Row label="Formato do vídeo">
        <Select value={c.format} onChange={set("format")} testId="reels-format" options={[["9:16", "Vertical 9:16"], ["1:1", "Quadrado 1:1"], ["16:9", "Horizontal 16:9"]]} />
      </Row>
      <Row label="Tamanho da miniatura">
        <Select value={c.thumb_size} onChange={set("thumb_size")} testId="reels-thumb-size" options={[["sm", "Pequena"], ["md", "Média"], ["lg", "Grande"]]} />
      </Row>
      <Row label="Quantidade de vídeos">
        <input type="number" min={1} max={50} value={c.count} onChange={(e) => onChange({ count: Number(e.target.value) || 1 })} className={selCls} data-testid="reels-count" />
      </Row>
      <Row label="Ordem de exibição">
        <Select value={c.sort} onChange={set("sort")} testId="reels-sort" options={[["order", "Ordem definida no cadastro"], ["recent", "Mais recentes primeiro"], ["featured", "Destaques primeiro"], ["random", "Aleatória"]]} />
      </Row>
      <Toggle label="Mostrar só vídeos em destaque" checked={!!c.featured_only} onChange={set("featured_only")} testId="reels-featured-only" />
      <Toggle label="Mostrar título nas miniaturas" checked={!!c.show_titles} onChange={set("show_titles")} testId="reels-show-titles" />
      <Toggle label="Prévia animada (sem som)" checked={!!c.autoplay_preview} onChange={set("autoplay_preview")} testId="reels-autoplay" />
      <Toggle label='Mostrar "Reels" no menu do site' checked={c.menu_enabled !== false} onChange={set("menu_enabled")} testId="reels-menu-enabled" />
      <Toggle label="Mostrar Reels no perfil das empresas" checked={c.show_on_business !== false} onChange={set("show_on_business")} testId="reels-on-business" />
    </div>
  );
};

export const DotsSettings = ({ section, onChange }) => {
  const d = { ...DOTS_DEFAULTS, ...(section.dots || {}) };
  const set = (k) => (v) => onChange({ dots: { ...d, [k]: v } });
  return (
    <div className="bg-white rounded-2xl border border-border p-4 mb-4" data-testid="dots-settings">
      <p className="text-sm font-bold text-slate-900">Estilo das bolinhas de navegação</p>
      <p className="text-xs text-muted-foreground mb-3">Vale para todos os carrosséis da página inicial. Ligue ou desligue as bolinhas em cada seção logo abaixo.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Row label="Formato">
          <Select value={d.shape} onChange={set("shape")} testId="dots-shape" options={[["circle", "Círculo"], ["square", "Quadrado"], ["pill", "Pílula"], ["line", "Linha"]]} />
        </Row>
        <Row label="Estilo">
          <Select value={d.style} onChange={set("style")} testId="dots-style" options={[["filled", "Preenchido"], ["outline", "Contorno"]]} />
        </Row>
        <Row label="Posição (somente nos banners)">
          <Select value={d.position} onChange={set("position")} testId="dots-position" options={[["below", "Abaixo do banner"], ["inside", "Sobre o banner"]]} />
        </Row>
        <Row label={`Tamanho: ${d.size}px`}>
          <input type="range" min={4} max={20} value={d.size} onChange={(e) => set("size")(Number(e.target.value))} data-testid="dots-size" />
        </Row>
        <Row label={`Espaçamento: ${d.spacing}px`}>
          <input type="range" min={2} max={24} value={d.spacing} onChange={(e) => set("spacing")(Number(e.target.value))} data-testid="dots-spacing" />
        </Row>
        <Row label="Cor">
          <input type="color" value={d.color} onChange={(e) => set("color")(e.target.value)} className="h-9 w-full rounded-lg border border-border" data-testid="dots-color" />
        </Row>
        <Row label="Cor da bolinha ativa">
          <div className="flex gap-2 items-center">
            <input type="color" value={d.active_color || "#FA4A0C"} onChange={(e) => set("active_color")(e.target.value)} className="h-9 flex-1 rounded-lg border border-border" data-testid="dots-active-color" />
            {d.active_color && <button type="button" onClick={() => set("active_color")("")} className="text-[11px] text-primary font-semibold" data-testid="dots-active-color-reset">Usar cor do site</button>}
          </div>
        </Row>
        <Toggle label="Bolinha ativa alongada" checked={!!d.active_grow} onChange={set("active_grow")} testId="dots-active-grow" />
        <div className="rounded-xl border border-border bg-slate-900/90 flex items-center justify-center py-3 lg:col-span-3" data-testid="dots-preview">
          <BannerDots count={4} active={1} config={d} testId="dots-preview-dots" />
        </div>
      </div>
    </div>
  );
};

export const CarouselSettings = ({ section, onChange }) => {
  const [secs, setSecs] = useState(String(section.autoplay_seconds || 4));
  const isBanner = section.key === "banners";
  const dots = { ...DOTS_DEFAULTS, ...(section.dots || {}) };
  const showDots = isBanner ? dots.enabled !== false : section.show_dots !== false;
  const setDots = (v) => onChange(isBanner ? { dots: { ...dots, enabled: v } } : { show_dots: v });
  return (
    <div className="mt-3 pl-9 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={`carousel-settings-${section.key}`}>
      <Toggle label="Mostrar bolinhas" checked={showDots} onChange={setDots} testId={`show-dots-${section.key}`} />
      <Toggle label="Mover sozinho" checked={section.autoplay !== false} onChange={(v) => onChange({ autoplay: v })} testId={`autoplay-${section.key}`} />
      <Row label="Trocar a cada (segundos)">
        <input type="number" min={2} max={20} value={secs} disabled={section.autoplay === false}
          onChange={(e) => {
            setSecs(e.target.value);
            const n = Number(e.target.value);
            if (n >= 2 && n <= 20) onChange({ autoplay_seconds: n });
          }}
          onBlur={() => {
            const n = Math.min(20, Math.max(2, Number(secs) || 4));
            setSecs(String(n));
            onChange({ autoplay_seconds: n });
          }}
          className={selCls} data-testid={`autoplay-seconds-${section.key}`} />
      </Row>
    </div>
  );
};
